import { Observable, of as observableOf, Subject } from "rxjs";

import { catchError, filter, map, tap } from "rxjs/operators";
import { TransactionNotifyService } from "../notify/transaction.notify.service";
import { Service } from "../base-service";
import { Helper, parseList } from "@helpers/functions";
import { InitService } from "@core-services/init.service";
import { FiatTransaction, Transaction } from "@models/transaction";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { Address, ErrorClass, Wallet } from "@models";
import { DestructorService } from "@core-services/destructor.service";
import { TranslationService } from "@core-services/translation.service";
import { InfoService } from "@core-services/info.service";

export abstract class WalletService<W extends Wallet, T extends Transaction> extends Service<W> {
	protected _loaded = false;

	protected transactionUpdateReceived: Subject<T | null> = new Subject<T | null>();

	protected abstract transactionNotifyService: TransactionNotifyService<T>;
	protected abstract apicallService: ApicallService;
	protected abstract infoService: InfoService;
	protected abstract translationService: TranslationService;

	private oldTx: T = null; // Used to prevent double api calls on socket event update

	protected constructor(
		protected initService: InitService,
		protected destructor: DestructorService,
		public name: string
	) {
		super(initService, destructor, name);
	}

	public abstract initWallets(force, silent): void;

	public abstract cancel(t: T): Promise<T>;

	public getTransactionUpdateReceived(): Observable<T> {
		return this.transactionUpdateReceived.asObservable();
	}

	public abstract socketInit(): void;

	public get loaded(): boolean {
		return this._loaded;
	}

	public getAddressesForWallet(wallet): Observable<W> {
		// Todo get rid of this function, only here until new wallet details allow for seperate wallet deletion
		const option = new ApicallOption();
		option.raw = true;
		return this.apicallService.get(`wallet/${wallet.getId()}/addresses`, option).pipe(
			map((addresses) => {
				wallet.addresses = parseList(addresses["data"], Address);
				return wallet;
			})
		);
	}

	public get list(): W[] {
		return this._list.getValue();
	}

	public set list(w: W[]) {
		this.initService.init(this.name);
		this._list.next(w);
	}

	public noBalance(): boolean {
		return this.list.filter((f: W) => f.getBalance() > 0).length === 0;
	}

	public getNonEmptyWallets(): W[] {
		return this.list.filter((w) => w.getBalance() > 0);
	}

	public cancelSub(call: Observable<T[]>, transaction: T, oldTransaction: T): Observable<T> {
		transaction.setStatus("canceled");
		return call.pipe(
			map((res) => res[0]),
			catchError((error) => {
				Object.assign(transaction, oldTransaction);
				Helper.handleError(error);

				return observableOf(null);
			}),
			filter((a) => !!a),
			tap((canceled) => {
				const txType = transaction instanceof FiatTransaction ? "fiat" : "crypto";
				this.infoService.setSuccess(
					this.translationService.translate("success_canceled_" + txType + "_wallet_transaction")
				);
				this.transactionNotifyService.show("cancel", canceled);
				transaction = canceled;
				this.updateReceived(transaction);
			})
		);
	}

	public resendEmail(transaction: T): void {
		let link = "";
		if (transaction instanceof FiatTransaction) {
			link = "fiatwallet/" + transaction.getFiatWalletId();
		} else {
			link = "wallet/" + transaction.getWalletId();
		}

		link += "/transaction/" + transaction.getId() + "/resendConfirmation";

		this.apicallService.get(link).subscribe(
			() => this.infoService.setInfo(this.translationService.translate("successfully_resent_confirm_email")),
			(e) => this.infoService.setError(e instanceof ErrorClass ? e : this.translationService.translate("error"))
		);
	}

	public abstract confirmTransaction(walletId: string, transactionId: string, code: string): Observable<T>;

	public transactionReceived(): void {
		this.updateReceived();
	}

	public updateReceived(t: T = null): void {
		if (t) {
			// Used to prevent double API call on update (api response + socket afterwards) (#1520)
			if (this.oldTx != null && t.getId() === this.oldTx.getId() && t.getStatus() === this.oldTx.getStatus()) {
				return;
			}
			this.oldTx = t; // Save last updated tx
			this.transactionUpdateReceived.next(t);
		} else {
			this.transactionUpdateReceived.next(null);
		}
	}
}
